import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          localStorage.setItem('accessToken', response.data.access_token);
          localStorage.setItem('refreshToken', response.data.refresh_token);
          
          originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data: { email: string; password: string }) => 
    api.post('/auth/login', data),
  
  register: (data: { username: string; email: string; password: string; display_name: string }) => 
    api.post('/auth/register', data),
  
  logout: () => 
    api.post('/auth/logout'),
  
  refresh: (refreshToken: string) => 
    api.post('/auth/refresh', { refresh_token: refreshToken }),
};

// Users API
export const usersAPI = {
  getUser: (userId: string) => 
    api.get(`/users/${userId}`),
  
  updateUser: (userId: string, data: any) => 
    api.put(`/users/${userId}`, data),
  
  getUserPosts: (userId: string, page = 1, limit = 20) => 
    api.get(`/users/${userId}/posts`, { params: { page, limit } }),
  
  follow: (userId: string) => 
    api.post(`/users/${userId}/follow`),
  
  unfollow: (userId: string) => 
    api.delete(`/users/${userId}/follow`),
  
  getFollowers: (userId: string, page = 1, limit = 20) => 
    api.get(`/users/${userId}/followers`, { params: { page, limit } }),
  
  getFollowing: (userId: string, page = 1, limit = 20) =>
    api.get(`/users/${userId}/following`, { params: { page, limit } }),

  rateTrust: (userId: string, rating: number) =>
    api.post(`/users/${userId}/rate-trust`, { rating }),

  getTrustVote: (userId: string) =>
    api.get(`/users/${userId}/trust-vote`),

  updateLocation: (latitude: number, longitude: number) =>
    api.post('/users/me/location', { latitude, longitude }),
};

// Posts API
export const postsAPI = {
  getFeed: (params: { latitude: number; longitude: number; scope?: string; page?: number; limit?: number }) =>
    api.get('/posts', { params }),

  create: (data: { content: string; image_url?: string; media_items?: any[]; latitude: number; longitude: number; location_name?: string }) =>
    api.post('/posts', data),
  
  getPost: (postId: string) => 
    api.get(`/posts/${postId}`),
  
  delete: (postId: string) => 
    api.delete(`/posts/${postId}`),
  
  like: (postId: string) => 
    api.post(`/posts/${postId}/like`),
  
  unlike: (postId: string) => 
    api.delete(`/posts/${postId}/like`),
  
  getComments: (postId: string, page = 1, limit = 20) => 
    api.get(`/posts/${postId}/comments`, { params: { page, limit } }),
  
  addComment: (postId: string, content: string, parentId?: string) =>
    api.post(`/posts/${postId}/comments`, { content, parent_id: parentId }),

  report: (postId: string, reason: string, notes?: string) =>
    api.post(`/posts/${postId}/report`, { reason, notes }),

  update: (postId: string, data: { content?: string; remove_media_ids?: string[]; add_media_items?: any[] }) =>
    api.put(`/posts/${postId}`, data),

  getEditHistory: (postId: string) =>
    api.get(`/posts/${postId}/history`),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post<{ image_url: string }>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  uploadMedia: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{
      url: string;
      original_url: string;
      thumbnail_url: string;
      media_type: string;
      width?: number;
      height?: number;
      duration_seconds?: number;
      file_size?: number;
    }>('/upload/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Messages API
export const messagesAPI = {
  getConversations: (page = 1, limit = 20) =>
    api.get('/conversations', { params: { page, limit } }),

  startConversation: (userId: string) =>
    api.post('/conversations', { user_id: userId }),

  getMessages: (conversationId: string, page = 1, limit = 30) =>
    api.get(`/conversations/${conversationId}/messages`, { params: { page, limit } }),

  sendMessage: (conversationId: string, content: string) =>
    api.post(`/conversations/${conversationId}/messages`, { content }),

  markAsRead: (conversationId: string) =>
    api.put(`/conversations/${conversationId}/read`),

  getUnreadCount: () =>
    api.get('/conversations/unread-count'),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: (days = 30) =>
    api.get('/admin/analytics/dashboard', { params: { days } }),
};

// Admin API
export const adminAPI = {
  getAllUsers: (page = 1, limit = 20) =>
    api.get('/admin/users', { params: { page, limit } }),

  updateTrustRating: (userId: string, rating: number, reason: string) =>
    api.put(`/admin/users/${userId}/rating`, { rating, reason }),

  getOnlineUsers: () =>
    api.get('/admin/online-users'),

  promoteToAdmin: (userId: string) =>
    api.put(`/admin/users/${userId}/promote`),

  demoteAdmin: (userId: string) =>
    api.put(`/admin/users/${userId}/demote`),
};

// Feeds API (admin)
export const feedsAPI = {
  getAll: (page = 1, limit = 50) =>
    api.get('/admin/feeds', { params: { page, limit } }),

  create: (data: { url: string; source_name: string; city?: string; state_province?: string; country?: string; is_active?: boolean }) =>
    api.post('/admin/feeds', data),

  update: (id: string, data: { url?: string; source_name?: string; city?: string; state_province?: string; country?: string; is_active?: boolean }) =>
    api.put(`/admin/feeds/${id}`, data),

  delete: (id: string) =>
    api.delete(`/admin/feeds/${id}`),

  preview: (id: string) =>
    api.get(`/admin/feeds/${id}/preview`),
};

export default api;
